import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Inbox, CreditCard, FileText, Users, LogOut, Link as LinkIcon } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/lib/auth';
import { logout } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const NAV = [
    { to: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
    { to: '/admin/consultations', label: 'Consultations', icon: Inbox },
    { to: '/admin/payments', label: 'Payments', icon: CreditCard },
    { to: '/admin/payment-links', label: 'Payment Links', icon: LinkIcon },
    { to: '/admin/leads', label: 'Leads', icon: Users },
    { to: '/admin/posts', label: 'Insights CMS', icon: FileText },
];

export default function AdminLayout({ children, title }) {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const onLogout = async () => {
        try {
            await logout();
        } catch (err) {
            // Surface the failure to the user instead of silently swallowing it
            // or logging to console (which has no value in production).
            toast.error('We could not contact the server. Your session has been cleared locally.');
        }
        setUser(null);
        toast.success('Signed out.');
        navigate('/admin/login', { replace: true });
    };
    return (
        <div className="min-h-screen bg-la-canvas text-la-text">
            <aside data-testid="admin-sidebar" className="fixed inset-y-0 left-0 w-60 border-r border-white/10 bg-la-panel/70 backdrop-blur-md hidden md:flex flex-col">
                <div className="px-5 py-5 border-b border-white/10">
                    <Logo size={32} />
                </div>
                <nav className="flex-1 px-3 py-4 space-y-1">
                    {NAV.map(({ to, label, icon: Icon, exact }) => (
                        <NavLink
                            key={to} to={to} end={!!exact}
                            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g,'-')}`}
                            className={({ isActive }) => cn(
                                'flex items-center gap-3 px-3 py-2 rounded-la-sm text-sm transition-colors',
                                isActive ? 'bg-white/[0.07] text-la-text border border-white/10' : 'text-la-text2 hover:text-la-text hover:bg-white/[0.04]',
                            )}
                        >
                            <Icon className="h-4 w-4" /> {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="px-3 py-3 border-t border-white/10">
                    <div className="flex items-center gap-2 px-2 py-2 text-xs text-la-text2">
                        <span className="truncate">{user?.email}</span>
                    </div>
                    <button onClick={onLogout} className="mt-1 w-full inline-flex items-center gap-2 px-3 py-2 rounded-la-sm text-sm text-la-text2 hover:text-la-text hover:bg-white/[0.04]" data-testid="admin-logout-button">
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>
            </aside>
            {/* Mobile top bar */}
            <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
                <Logo size={32} withWordmark={false} />
                <button onClick={onLogout} className="text-xs text-la-text2 inline-flex items-center gap-1"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
            </div>
            <main className="md:ml-60 px-4 sm:px-6 py-6 md:py-8">
                {title && (
                    <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="meta-eyebrow text-la-gold">Admin</div>
                            <h1 className="mt-2 font-display text-3xl text-la-text">{title}</h1>
                        </div>
                        <Link to="/" className="text-xs text-la-text2 hover:text-la-text underline-grow">Back to public site</Link>
                    </div>
                )}
                {children}
            </main>
        </div>
    );
}
